/**
 * The page on screen, mirrored in the URL hash (`#/breaking-news`).
 *
 * Without it a reload — or the sign-in redirect after a token expired — always
 * landed on the Article Generator, and the browser's back button left the app.
 * The hash, not the path, so the backend's SPA fallback and the MSAL redirect
 * URI (the site root) need no changes. MSAL has already consumed its own
 * response hash by the time the app mounts: `Root` awaits
 * `handleRedirectPromise()` first. Anything that is not a known view is ignored.
 */

import { useCallback, useEffect, useState } from 'react'

import { DEFAULT_VIEW, isView, type View } from '../navigation'

const PREFIX = '#/'

export function viewFromHash(hash: string): View | null {
    if (!hash.startsWith(PREFIX)) return null
    let candidate: string
    try {
        candidate = decodeURIComponent(hash.slice(PREFIX.length))
    } catch {
        return null
    }
    return isView(candidate) ? candidate : null
}

export function useActiveView(): [View, (view: View) => void] {
    const [view, setView] = useState<View>(() => viewFromHash(window.location.hash) ?? DEFAULT_VIEW)

    useEffect(() => {
        // pushState does not fire hashchange, but going back does fire popstate;
        // a hash typed into the address bar fires hashchange. Listen to both.
        const sync = () => {
            const next = viewFromHash(window.location.hash)
            if (next) setView(next)
        }
        window.addEventListener('popstate', sync)
        window.addEventListener('hashchange', sync)
        return () => {
            window.removeEventListener('popstate', sync)
            window.removeEventListener('hashchange', sync)
        }
    }, [])

    const navigate = useCallback((next: View) => {
        setView(next)
        const hash = PREFIX + next
        if (window.location.hash !== hash) {
            window.history.pushState(null, '', hash)
        }
    }, [])

    return [view, navigate]
}
